import { useState, useCallback, useEffect } from 'react';
import {
  getBudgetHealthThresholds,
  fetchBudgetHealthThresholds,
  saveBudgetHealthThresholds,
  DEFAULT_BUDGET_HEALTH_THRESHOLDS,
  type BudgetHealthThresholds,
} from '@/lib/budget-health-thresholds-storage';
import { getAuthConfig } from '@/lib/auth-fetch';

export interface UseBudgetHealthThresholdsReturn {
  thresholds: BudgetHealthThresholds;
  hasUnsavedChanges: boolean;
  isSaving: boolean;
  updateThreshold: (key: keyof Omit<BudgetHealthThresholds, 'lastUpdated'>, value: number | boolean | string) => void;
  saveThresholds: () => Promise<void>;
  resetToDefaults: () => void;
}

export function useBudgetHealthThresholds(): UseBudgetHealthThresholdsReturn {
  const [thresholds, setThresholds] = useState<BudgetHealthThresholds>(getBudgetHealthThresholds);
  const [savedThresholds, setSavedThresholds] = useState<BudgetHealthThresholds>(getBudgetHealthThresholds);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    const { instanceId } = getAuthConfig();
    if (!instanceId) return;

    let cancelled = false;
    fetchBudgetHealthThresholds().then((fetched) => {
      if (!cancelled) {
        setThresholds(fetched);
        setSavedThresholds(fetched);
      }
    });
    return () => { cancelled = true; };
  }, []);

  const hasUnsavedChanges =
    thresholds.costAmberThreshold !== savedThresholds.costAmberThreshold ||
    thresholds.costRedThreshold !== savedThresholds.costRedThreshold ||
    thresholds.scheduleAmberGap !== savedThresholds.scheduleAmberGap ||
    thresholds.scheduleRedGap !== savedThresholds.scheduleRedGap ||
    thresholds.hoursAmberGap !== savedThresholds.hoursAmberGap ||
    thresholds.hoursRedGap !== savedThresholds.hoursRedGap ||
    thresholds.wipThresholdMode !== savedThresholds.wipThresholdMode ||
    thresholds.wipAmberThreshold !== savedThresholds.wipAmberThreshold ||
    thresholds.wipRedThreshold !== savedThresholds.wipRedThreshold ||
    thresholds.wipAmberPct !== savedThresholds.wipAmberPct ||
    thresholds.wipRedPct !== savedThresholds.wipRedPct ||
    thresholds.billingAmberGap !== savedThresholds.billingAmberGap ||
    thresholds.billingRedGap !== savedThresholds.billingRedGap ||
    // Dimension 5 — Margin
    thresholds.marginAmberThreshold !== savedThresholds.marginAmberThreshold ||
    thresholds.marginRedThreshold !== savedThresholds.marginRedThreshold ||
    thresholds.marginEnabled !== savedThresholds.marginEnabled;

  const updateThreshold = useCallback(
    (key: keyof Omit<BudgetHealthThresholds, 'lastUpdated'>, value: number | boolean | string) => {
      setThresholds((prev) => ({ ...prev, [key]: value }));
    },
    []
  );

  const saveThresholds = useCallback(async () => {
    setIsSaving(true);
    try {
      await saveBudgetHealthThresholds(thresholds);
      setSavedThresholds(thresholds);
    } finally {
      setIsSaving(false);
    }
  }, [thresholds]);

  const resetToDefaults = useCallback(() => {
    setThresholds({ ...DEFAULT_BUDGET_HEALTH_THRESHOLDS });
  }, []);

  return {
    thresholds,
    hasUnsavedChanges,
    isSaving,
    updateThreshold,
    saveThresholds,
    resetToDefaults,
  };
}
